import React, { Suspense, useDeferredValue, useId, useTransition } from 'react';
import { PersistGate } from 'redux-persist/integration/react';
import { Persistor } from 'redux-persist';
import { useDeferredRehydration } from '../hooks/useDeferredRehydration';

interface PersistGateModernProps {
  persistor: Persistor;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Modern PersistGate using React 18's latest concurrent features
 * - useDeferredValue for non-blocking updates
 * - useTransition for priority scheduling
 * - Suspense integration ready
 */
export const PersistGateModern: React.FC<PersistGateModernProps> = ({ 
  persistor, 
  children,
  fallback = null
}) => {
  const id = useId();
  const { isRehydrated, isPending } = useDeferredRehydration(persistor);
  

  // Always render children, but wrap with PersistGate when ready
  const content = isRehydrated ? (
    <PersistGate 
      loading={fallback}
      persistor={persistor}
    >
      {children}
    </PersistGate>
  ) : (
    <>{children}</>
  );

  // Wrap in Suspense boundary for future streaming SSR support
  return (
    <Suspense fallback={fallback}>
      {content}
    </Suspense>
  );
};

// Export a lazy version for code splitting if needed
export const LazyPersistGateModern = React.lazy(() => 
  Promise.resolve({ default: PersistGateModern })
);